import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  useTheme
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { tokens } from "../../../theme";
import {
  ApplicationStatus,
  getStatusStyles,
  STATUS_LABELS,
} from "../StreamFundApplication";

interface ApplicationTopBarProps {
  status: ApplicationStatus;
  onBack: () => void;
  onStatusChange: (value: ApplicationStatus) => void;
  onConvert: () => void;
  convertDisabled?: boolean;
  convertedBooking?: { id: number; reference_number: string | null } | null;
  approveDisabled?: boolean;
  isAdmin?: boolean;
}

export default function ApplicationTopBar({
  status,
  onBack,
  onStatusChange,
  onConvert,
  convertDisabled = false,
  convertedBooking = null,
  approveDisabled = false,
  isAdmin = false,
}: ApplicationTopBarProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const statusStyle = getStatusStyles(status);

  return (
    <Box
      sx={{
        mb: 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        sx={{
          minWidth: "auto",
          px: 2,
          py: 1.1,
          borderRadius: "999px",
          bgcolor: colors.primary[800],
          color: colors.primary[400],
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",
          border: `1px solid ${colors.primary[400]}`,
          "&:hover": { borderColor: `${colors.primary[400]}44` },
        }}
      >
        BACK
      </Button>

      <Stack direction="row" spacing={2} alignItems="center">
        {!convertedBooking && (
          <>
            <Chip
              label={STATUS_LABELS[status]}
              size="small"
              sx={{
                ...statusStyle,
                fontWeight: 700,
                fontSize: 11,
                height: 28,
                borderRadius: "999px",
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
            <FormControl size="small">
              <Select
                value={status}
                onChange={(e) =>
                  onStatusChange(e.target.value as ApplicationStatus)
                }
                input={<OutlinedInput notched={false} />}
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={{
                  minWidth: 190,
                  height: 40,
                  borderRadius: "999px",
                  bgcolor: colors.primary[800],
                  color: colors.primary[400],
                  fontWeight: 500,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: `${colors.primary[400]}`,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${colors.primary[400]}44`,
                  },
                }}
              >
                <MenuItem value="submitted" disabled>
                  Pending Decision
                </MenuItem>
                {isAdmin && (
                  <MenuItem value="approved" disabled={approveDisabled}>
                    Approve
                  </MenuItem>
                )}
                <MenuItem value="under_review">Request Info</MenuItem>
                {isAdmin && <MenuItem value="rejected">Decline</MenuItem>}
              </Select>
            </FormControl>
          </>
        )}
        {convertedBooking ? (
          <Button
            component={RouterLink}
            to={`/bookings/${convertedBooking.id}`}
            sx={{
              minWidth: "auto",
              px: 2,
              py: 1.1,
              borderRadius: "999px",
              bgcolor: "#4BA66322",
              color: "#4BA663",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              border: "1px solid #4BA66344",
              "&:hover": { borderColor: "#4BA663" },
            }}
          >
            Converted - View Booking
          </Button>
        ) : (
          <Button
            onClick={onConvert}
            disabled={convertDisabled}
            sx={{
              minWidth: "auto",
              px: 2,
              py: 1.1,
              borderRadius: "999px",
              bgcolor: colors.primary[800],
              color: colors.primary[400],
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              border: `1px solid ${colors.primary[400]}`,
              "&:hover": { borderColor: `${colors.primary[400]}44` },
              "&.Mui-disabled": {
                opacity: 0.5,
                borderColor: `${colors.primary[400]}22`,
                color: `${colors.primary[400]}66`,
              },
            }}
          >
            Convert to booking
          </Button>
        )}
      </Stack>
    </Box>
  );
}
